/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { StarOutline, DoNotDisturb, WebAssetOff, Link } from '@mui/icons-material';
import { List, ListItem, Tooltip, Typography, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import type { PluginBadgeProps, PluginBadgeGenericProps } from './types';

export function PluginPremiumBadge( { tooltipTitle, tooltipList, onlyIcon, isInline }: PluginBadgeProps ): ReactNode {
	const { palette } = useTheme();

	return (
		<PluginBadgeGeneric
			isInline={ isInline }
			tooltipTitle={ tooltipTitle }
			tooltipList={ tooltipList }
			onlyIcon={ onlyIcon }
			Icon={ StarOutline }
			color={ palette.warning.dark }
			backgroundColor={ palette.warning[ 100 ]! }
			badgeText={ 'Premium inside' }
		/>
	);
}

export function PluginConflictBadge( { tooltipTitle, tooltipList, onlyIcon }: PluginBadgeProps ): ReactNode {
	const { palette } = useTheme();

	return (
		<PluginBadgeGeneric
			tooltipTitle={ tooltipTitle || 'Conflicting features:' }
			tooltipList={ tooltipList }
			onlyIcon={ onlyIcon }
			Icon={ DoNotDisturb }
			color={ palette.text.secondary }
			backgroundColor={ palette.grey[ 200 ] }
			badgeText={ 'Conflict' }
		/>
	);
}

export function PluginDisabledBadge( { tooltipTitle, tooltipList, onlyIcon }: PluginBadgeProps ): ReactNode {
	const { palette } = useTheme();

	return (
		<PluginBadgeGeneric
			tooltipTitle={
				tooltipTitle || ( tooltipList ?
					'This feature is not supported for the following editor types:' :
					'Contains disabled features.' )
			}
			tooltipList={ tooltipList }
			onlyIcon={ onlyIcon }
			Icon={ WebAssetOff }
			color={ palette.text.secondary }
			backgroundColor={ palette.grey[ 200 ] }
			badgeText={ 'Disabled' }
		/>
	);
}

export function PluginDependsBadge( { tooltipTitle, tooltipList, onlyIcon }: PluginBadgeProps ): ReactNode {
	const { palette } = useTheme();

	return (
		<PluginBadgeGeneric
			tooltipTitle={ tooltipTitle }
			tooltipList={ tooltipList }
			onlyIcon={ onlyIcon }
			Icon={ Link }
			color={ palette.text.secondary }
			backgroundColor={ palette.grey[ 200 ] }
			badgeText={ 'Dependency' }
		/>
	);
}

function PluginBadgeGeneric( {
	tooltipTitle,
	tooltipList,
	onlyIcon,
	Icon,
	color,
	backgroundColor,
	badgeText,
	isInline
}: PluginBadgeGenericProps ): ReactNode {
	return (
		<Tooltip disableInteractive title={ getTooltip( { tooltipTitle, tooltipList } ) }>
			<Typography
				component={ 'span' }
				fontWeight='400'
				fontSize='12px'
				color={ color }
				padding={ onlyIcon ? '0 4px' : '0 8px' }
				height='20px'
				borderRadius='10px'
				whiteSpace='nowrap'
				sx={ {
					display: isInline ? 'inline-flex' : 'flex',
					gap: '2px',
					alignItems: 'center',
					backgroundColor
				} }
			>
				<Icon sx={ { width: '12px', height: '12px' } }/>
				{ !onlyIcon && ( <span>{ badgeText }</span> ) }
			</Typography>
		</Tooltip>
	);
}

function getTooltip( {
	tooltipTitle,
	tooltipList
}: {
	tooltipTitle: PluginBadgeProps['tooltipTitle'];
	tooltipList: PluginBadgeProps['tooltipList'];
} ): ReactNode | null {
	if ( !tooltipTitle && !tooltipList ) {
		return null;
	}

	return (
		<div>
			{ tooltipTitle && <span>{ tooltipTitle }</span> }
			{ tooltipList &&
			<List
				sx={ {
					marginTop: tooltipTitle ? '12px' : undefined,

					'& li.MuiListItem-root::before': {
						content: '\'\u2022\'', // Bullet character
						fontSize: '1.2rem',
						lineHeight: '1rem',
						paddingRight: '3px'
					}
				} }
			>
				{ tooltipList.map( tooltipItem => (
					<ListItem key={ tooltipItem + '-wrapper' }>{ tooltipItem }</ListItem>
				) ) }
			</List>
			}
		</div>
	);
}
